import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { buildTransformedUrl, safeHttpUrl, LIGHTBOX_IMAGE } from "@/lib/images";
import { photoCaption, useImageFallback } from "./media-helpers";
import type { MediaItem } from "@adva/types";

/** Title-case a `photo_stage` enum value for the caption boundary (§4.3). */
function stageLabel(stage: MediaItem["photo_stage"]): string | null {
  if (!stage) return null;
  return stage.charAt(0).toUpperCase() + stage.slice(1);
}

/**
 * Lightbox caption (§I4.2 canonical order, location-first): `{caption} ·
 * {Stage}`. With the location+scope fused into the stored `caption` string,
 * the stage trails; the segment + middot are omitted when a part is absent
 * (§I4.3).
 */
function lightboxCaption(item: MediaItem): string | null {
  const parts = [photoCaption(item), stageLabel(item.photo_stage)].filter(
    (p): p is string => !!p,
  );
  return parts.length ? parts.join(" · ") : null;
}

/** The lightbox image — object-contain, its own §I7 fallback chain. Keyed by
 * item id at the call site so navigating photos remounts with fresh state. */
function LightboxImage({ item }: { item: MediaItem }) {
  const { src, onError } = useImageFallback(item.public_url, LIGHTBOX_IMAGE);
  if (!src) {
    return (
      <div className="flex aspect-[3/2] w-full items-center justify-center bg-[color:var(--color-surface)]">
        <ImageIcon
          className="size-24 text-[color:var(--color-text-lo)]"
          aria-hidden
        />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={photoCaption(item) ?? "Job-site photo"}
      onError={onError}
      loading="lazy"
      className="max-h-[80vh] w-full bg-[color:var(--color-surface)] object-contain"
    />
  );
}

/**
 * Shared single-instance lightbox for the gallery (and, post-tracer-bullet,
 * the Overview strip). Controlled by a flat `index` into a flattened, ordered
 * `photos` array (stage order then display_order). Built on the installed Radix
 * Dialog — focus trap, Esc, aria-modal free (§3.1). Prev/next are bounds-
 * disabled (no circular wrap — "end of evidence" is information); ArrowLeft/
 * ArrowRight navigate; neighbors preload (±1) for zero-latency nav.
 */
export function PhotoLightbox({
  photos,
  index,
  onIndexChange,
}: {
  photos: MediaItem[];
  index: number | null;
  onIndexChange: (index: number | null) => void;
}) {
  const open = index != null && index >= 0 && index < photos.length;
  const active = open ? photos[index] : null;

  // Embla (via @shadcn/carousel) backs touch + trackpad-drag SWIPE; the flanking
  // arrows + keyboard remain the controlled-index source of truth. Two-way sync:
  // a swipe settles → parent index; an arrow/keyboard move → embla scrollTo.
  const [api, setApi] = useState<CarouselApi>();
  const positioned = useRef(false);

  // swipe settle → parent index
  useEffect(() => {
    if (!api) return;
    const onSelect = () => onIndexChange(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onIndexChange]);

  // parent index (arrow/keyboard) → embla: jump to the opening photo on first
  // mount, animate on subsequent moves. (Equality guard prevents a swipe→select
  // →index update from fighting the scroll it just produced.)
  useEffect(() => {
    if (!api || index == null) return;
    if (!positioned.current) {
      api.scrollTo(index, true);
      positioned.current = true;
    } else if (api.selectedScrollSnap() !== index) {
      api.scrollTo(index);
    }
  }, [api, index]);

  // reset the "first open" flag when the lightbox closes so the next open jumps
  useEffect(() => {
    if (index == null) positioned.current = false;
  }, [index]);

  // Preload neighbors so prev/next paint instantly (the one motion worth
  // spending on, §2 lightbox brief).
  useEffect(() => {
    if (index == null) return;
    for (const i of [index - 1, index + 1]) {
      if (i < 0 || i >= photos.length) continue;
      const url =
        buildTransformedUrl(photos[i].public_url, LIGHTBOX_IMAGE) ??
        safeHttpUrl(photos[i].public_url);
      if (url) {
        const img = new Image();
        img.src = url;
      }
    }
  }, [index, photos]);

  // Drive ArrowLeft/ArrowRight from a document-level listener rather than a
  // DialogContent onKeyDown. When prev/next reaches a bound and gets the native
  // `disabled` attribute, the browser blurs it to document.body — OUTSIDE the
  // portal'd content — and Radix's FocusScope does not reclaim focus on a
  // disable-blur, so a content-scoped handler would go dead for keyboard users
  // at exactly the boundary (§3.1 — keyboard must complete the task). A window
  // listener works regardless of where focus landed. Esc stays Radix-owned.
  useEffect(() => {
    if (index == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        onIndexChange(index - 1);
      } else if (e.key === "ArrowRight" && index < photos.length - 1) {
        e.preventDefault();
        onIndexChange(index + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onIndexChange]);

  const go = (next: number) => {
    if (next >= 0 && next < photos.length) onIndexChange(next);
  };

  const caption = active ? lightboxCaption(active) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onIndexChange(null)}>
      <DialogContent className="max-w-5xl gap-0 overflow-hidden border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-0">
        <DialogTitle className="sr-only">{caption ?? "Photo"}</DialogTitle>
        <DialogDescription className="sr-only">
          Job-site photo viewer
        </DialogDescription>

        <div className="relative">
          {/* Swipe track — one slide per photo (touch + trackpad drag, no
              wrap). Caption/counter/title stay driven by the controlled
              `index`, synced both ways with embla above. */}
          <Carousel setApi={setApi} opts={{ loop: false }} className="w-full">
            <CarouselContent className="ml-0">
              {photos.map((photo) => (
                <CarouselItem key={photo.id} className="pl-0">
                  <LightboxImage item={photo} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Nav flanks the image, left/right edge, vertically centered. Solid
              token-surface circles — never a translucent scrim over the photo
              (§I3.6 rationale: overlays fail contrast on variable-brightness
              images). Bounds-disabled, no circular wrap. */}
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Previous photo"
            disabled={index == null || index <= 0}
            onClick={() => index != null && go(index - 1)}
            className="absolute left-3 top-1/2 z-10 size-9 -translate-y-1/2 rounded-full border border-[color:var(--color-border)]"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Next photo"
            disabled={index == null || index >= photos.length - 1}
            onClick={() => index != null && go(index + 1)}
            className="absolute right-3 top-1/2 z-10 size-9 -translate-y-1/2 rounded-full border border-[color:var(--color-border)]"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>

        <div className="flex items-center gap-3 border-t border-[color:var(--color-border)] px-4 py-3">
          {caption && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="min-w-0 flex-1 truncate font-[family-name:var(--font-family-body)] text-[length:var(--font-size-label)] leading-[var(--line-height-label)] text-[color:var(--color-text-md)]">
                    {caption}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{caption}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <span className="ml-auto shrink-0 typo-mono text-[color:var(--color-text-lo)]">
            {index != null ? index + 1 : 0} / {photos.length}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
